/**
 * Filter merge tags based on the context/field type
 * This ensures users only see relevant merge tags for each field
 */

export type MergeTagContext =
	| 'image-src' // Image source URLs
	| 'image-link' // Image link/href URLs
	| 'button-link' // Button link/href URLs
	| 'button-text' // Button text content
	| 'social-link' // Social media link URLs
	| 'social-website-link'
	| 'social-facebook-link'
	| 'social-instagram-link'
	| 'social-x-link'
	| 'social-linkedin-link'
	| 'social-linkedin-business-link'
	| 'social-youtube-link'
	| 'social-tiktok-link'
	| 'social-pinterest-link'
	| 'social-yelp-link'
	| 'text-content' // Rich text content (show all)
	| 'no-image-src' // All fields except image sources (default dropdown)
	| 'link-href' // Generic link href
	| 'subject-preview'; // Subject / preview text inputs (contact + date only)

/**
 * Get filtered merge tags based on context
 * @param mergeTags - Full merge tags object from editor props
 * @param context - The context/field type
 * @returns Filtered merge tags object with only relevant fields
 */

// These image fields have been excluded from the default dropdown, as they are only relevant for image source URLs
const LISTING_IMAGE_FIELDS = [
	'image_data_0',
	'image_data_1',
	'image_data_2',
	'image_data_3',
	'image_data_4',
	'image_data_5',
	'image_data_6',
	'image_data_7',
	'image_data_8',
	'image_data_9',
	'imagePath',
	'thumbPath',
	'imageUrl',
	'image',
	'photo',
	'listingImage',
	'mainImage',
	'primaryImage',
];
const USER_IMAGE_FIELDS = [
	'profileImagePortrait',
	'profileImageSquare',
	'profileImageCircle',
	'imageUrl',
	'profileImage',
	'profilePicture',
	'avatar',
	'photo',
	'image',
	'logo',
	'companyLogo',
	'logoLightUrl',
	'logoDarkUrl',
	'officeLogoLightUrl',
	'officeLogoDarkUrl',
];
const SOCIAL_USER_FIELD_MAP = {
	'social-website-link': 'websiteUrl',
	'social-facebook-link': 'facebookUrl',
	'social-instagram-link': 'instagramUrl',
	'social-x-link': 'twitterUrl',
	'social-linkedin-link': 'linkedInUrl',
	'social-linkedin-business-link': 'linkedInBusinessUrl',
	'social-youtube-link': 'youtubeUrl',
	'social-tiktok-link': 'tiktokUrl',
	'social-pinterest-link': 'pinterestUrl',
	'social-yelp-link': 'yelpUrl',
} as const;

export function getMergeTagsForContext(
	mergeTags: Record<string, any>,
	context: MergeTagContext = 'no-image-src',
): Record<string, any> {
	if (!mergeTags || context === 'text-content') {
		// For text content, return all merge tags
		return mergeTags;
	}

	if (context === 'no-image-src') {
		// Default dropdown: show all fields except image source URLs
		const result: Record<string, any> = { ...mergeTags };
		if (result.listing) {
			result.listing = { ...result.listing };
			LISTING_IMAGE_FIELDS.forEach(key => delete result.listing[key]);
		}
		if (result.user) {
			result.user = { ...result.user };
			USER_IMAGE_FIELDS.forEach(key => delete result.user[key]);
		}
		return result;
	}

	const filtered: Record<string, any> = {};

	switch (context) {
		case 'subject-preview':
			// Subject / preview text: contact + date fields only.
			// - Listing data is excluded: there is no single listing in scope for a
			//   subject line, so we can't know which listing to resolve.
			// - User data and Legal & Compliance are excluded: no strong use case.
			// These groups remain available in all other dynamic field dropdowns.
			if (mergeTags.contact) {
				filtered.contact = { ...mergeTags.contact };
			}
			if (mergeTags.current_date) {
				filtered.current_date = { ...mergeTags.current_date };
			}
			break;

		case 'image-src':
			// Image sources - only show image URL fields, not text/data fields
			if (mergeTags.listing) {
				filtered.listing = {};
				for (let index = 0; index <= 9; index += 1) {
					const key = `image_data_${index}`;
					filtered.listing[key] = mergeTags.listing[key] ?? '';
				}
			}
			if (mergeTags.user) {
				filtered.user = {
					profileImagePortrait:
						mergeTags.user.profileImagePortrait ??
						mergeTags.user.profileImage ??
						mergeTags.user.imageUrl ??
						mergeTags.user.photo ??
						'',
					profileImageSquare:
						mergeTags.user.profileImageSquare ??
						mergeTags.user.profilePicture ??
						'',
					profileImageCircle:
						mergeTags.user.profileImageCircle ??
						mergeTags.user.avatar ??
						'',
					logoLightUrl: mergeTags.user.logoLightUrl ?? '',
					logoDarkUrl: mergeTags.user.logoDarkUrl ?? '',
					officeLogoLightUrl: mergeTags.user.officeLogoLightUrl ?? '',
					officeLogoDarkUrl: mergeTags.user.officeLogoDarkUrl ?? '',
				};
			}
			// Contact fields should NOT appear in image blocks - no profile pictures for contacts
			break;

		case 'image-link':
		case 'button-link':
		case 'link-href':
			// Link URLs - show URL/website fields only
			if (mergeTags.listing) {
				filtered.listing = {};
				if (mergeTags.listing.url) filtered.listing.url = mergeTags.listing.url;
				if (mergeTags.listing.virtualTourUrl)
					filtered.listing.virtualTourUrl = mergeTags.listing.virtualTourUrl;
				if (mergeTags.listing.videoTourUrl)
					filtered.listing.videoTourUrl = mergeTags.listing.videoTourUrl;
				if (mergeTags.listing.agentWebsiteAddress)
					filtered.listing.agentWebsiteAddress = mergeTags.listing.agentWebsiteAddress;
				if (mergeTags.listing.secondAgentWebsiteAddress)
					filtered.listing.secondAgentWebsiteAddress =
						mergeTags.listing.secondAgentWebsiteAddress;
			}
			if (mergeTags.user) {
				filtered.user = {};
				if (mergeTags.user.website) filtered.user.website = mergeTags.user.website;
				if (mergeTags.user.companyWebsite)
					filtered.user.companyWebsite = mergeTags.user.companyWebsite;
			}
			break;

		case 'button-text':
			// Button text - show text content fields
			if (mergeTags.listing) {
				filtered.listing = {};
				// Include text fields that might be useful in buttons
				if (mergeTags.listing.status) filtered.listing.status = mergeTags.listing.status;
				if (mergeTags.listing.listPrice)
					filtered.listing.listPrice = mergeTags.listing.listPrice;
				if (mergeTags.listing.address)
					filtered.listing.address = mergeTags.listing.address;
				if (mergeTags.listing.city) filtered.listing.city = mergeTags.listing.city;
			}
			if (mergeTags.contact) {
				filtered.contact = {};
				// Include contact name fields for personalization
				if (mergeTags.contact.firstName)
					filtered.contact.firstName = mergeTags.contact.firstName;
				if (mergeTags.contact.lastName)
					filtered.contact.lastName = mergeTags.contact.lastName;
				if (mergeTags.contact.fullName)
					filtered.contact.fullName = mergeTags.contact.fullName;
			}
			break;

		case 'social-link':
			filtered.user = {
				websiteUrl: mergeTags.user?.websiteUrl ?? '',
				website: mergeTags.user?.website ?? '',
				companyWebsite: mergeTags.user?.companyWebsite ?? '',
				teamWebsiteAddress: mergeTags.user?.teamWebsiteAddress ?? '',
				officeDomain: mergeTags.user?.officeDomain ?? '',
				facebookUrl: mergeTags.user?.facebookUrl ?? '',
				instagramUrl: mergeTags.user?.instagramUrl ?? '',
				twitterUrl: mergeTags.user?.twitterUrl ?? '',
				linkedInUrl: mergeTags.user?.linkedInUrl ?? '',
				linkedInBusinessUrl: mergeTags.user?.linkedInBusinessUrl ?? '',
				youtubeUrl: mergeTags.user?.youtubeUrl ?? '',
				tiktokUrl: mergeTags.user?.tiktokUrl ?? '',
				pinterestUrl: mergeTags.user?.pinterestUrl ?? '',
				yelpUrl: mergeTags.user?.yelpUrl ?? '',
			};
			break;

		case 'social-website-link':
			if (mergeTags.user) {
				filtered.user = {
					websiteUrl: mergeTags.user.websiteUrl ?? '',
					website: mergeTags.user.website ?? '',
					companyWebsite: mergeTags.user.companyWebsite ?? '',
					teamWebsiteAddress: mergeTags.user.teamWebsiteAddress ?? '',
					officeDomain: mergeTags.user.officeDomain ?? '',
				};
			}
			break;

		case 'social-facebook-link':
		case 'social-instagram-link':
		case 'social-x-link':
		case 'social-linkedin-link':
		case 'social-linkedin-business-link':
		case 'social-youtube-link':
		case 'social-pinterest-link':
		case 'social-yelp-link':
		case 'social-tiktok-link': {
			const fieldKey = SOCIAL_USER_FIELD_MAP[context];
			filtered.user = {
				[fieldKey]: mergeTags.user?.[fieldKey] ?? '',
			};
			break;
		}

		default:
			return mergeTags;
	}

	// Remove empty groups
	Object.keys(filtered).forEach(key => {
		if (Object.keys(filtered[key]).length === 0) {
			delete filtered[key];
		}
	});

	return filtered;
}
